import { create } from 'zustand';

/**
 * Map-based breadcrumb override store.
 * Any component can override the breadcrumb label for its route path.
 * Supports multiple simultaneous overrides for nested dynamic routes.
 *
 * Usage:
 *   const setOverride = useBreadcrumbOverride((s) => s.setOverride);
 *   useEffect(() => {
 *     setOverride('/w/dashboard/profile/abc', 'Acme Corp');
 *     return () => setOverride('/w/dashboard/profile/abc', null);
 *   }, []);
 */
interface BreadcrumbOverrideState {
  overrides: Map<string, string>;
  setOverride: (path: string, label: string | null) => void;
}

export const useBreadcrumbOverride = create<BreadcrumbOverrideState>()((set) => ({
  overrides: new Map(),
  setOverride: (path, label) =>
    set((state) => {
      const next = new Map(state.overrides);
      if (label === null) {
        next.delete(path);
      } else {
        next.set(path, label);
      }
      return { overrides: next };
    }),
}));
